import { toArray } from "./functions";

export type BookingFormPassport = {
  Number?: string;
  Validity?: string;
  ValidityDate?: string;
  Citizenship?: string;
  CountryOfIssue?: string;
  Issued?: string;
};

type BookingFormFields = Record<string, { value?: string }>;

type PassportFieldUpdatesParams = {
  bookingFormFields: BookingFormFields;
  passportNumberFieldName: string;
  passport?: BookingFormPassport;
  manualPassportNumber?: string;
};

export type PassportFieldUpdate = {
  name: string;
  value: string;
};

export type PassportSelectOption = {
  label: string;
  value: string;
  passport?: BookingFormPassport;
};

type PassportFieldKey =
  | "number"
  | "validity"
  | "citizenship"
  | "countryOfIssue";

const PASSPORT_NUMBER_FIELD_SUFFIXES = [
  "passenger_passport_number",
  "PassportNumber",
];

const PASSPORT_FIELD_KEYS: PassportFieldKey[] = [
  "number",
  "validity",
  "citizenship",
  "countryOfIssue",
];

const PASSPORT_FIELD_ALIASES: Record<PassportFieldKey, string[]> = {
  number: PASSPORT_NUMBER_FIELD_SUFFIXES,
  validity: ["passenger_passport_validity", "PassportExpiryDate"],
  citizenship: ["passenger_citizenship", "Citizenship"],
  countryOfIssue: [
    "passenger_passport_country_of_issue",
    "PassportCountryOfIssue",
  ],
};

export function isBookingFormPassportNumberField(fieldName?: string): boolean {
  return PASSPORT_NUMBER_FIELD_SUFFIXES.some((suffix) =>
    fieldName?.endsWith(suffix)
  );
}

export function getPassengerIndexFromBookingFieldName(fieldName?: string) {
  const index = Number(fieldName?.split("-")[0]);
  return Number.isNaN(index) ? null : index;
}

export function getCbtTravelerPassportsForField({
  cbt,
  fieldName,
}: {
  cbt: any;
  fieldName: string;
}): BookingFormPassport[] {
  const passengerIndex = getPassengerIndexFromBookingFieldName(fieldName);
  if (passengerIndex === null) {
    return [];
  }

  const travelerId = toArray(cbt?.passengersForm?.travellers)[passengerIndex];
  if (!travelerId) {
    return [];
  }

  const cbtTravelerUser = cbt?.usersCache?.[travelerId];
  if (cbtTravelerUser?.Guest || !Array.isArray(cbtTravelerUser?.Passports)) {
    return [];
  }

  return cbtTravelerUser.Passports.filter((passport) => passport?.Number);
}

export function getPassportSelectOptions(passports: BookingFormPassport[]) {
  return passports.map((passport) => ({
    label: passport.Number || "",
    value: passport.Number || "",
    passport,
  }));
}

export function getPassportFieldUpdates({
  bookingFormFields,
  passportNumberFieldName,
  passport,
  manualPassportNumber,
}: PassportFieldUpdatesParams): PassportFieldUpdate[] {
  const fieldNames = getPassportFieldNames({
    bookingFormFields,
    passportNumberFieldName,
  });

  if (manualPassportNumber !== undefined) {
    return getManualPassportUpdates({ fieldNames, manualPassportNumber });
  }

  if (!passport) {
    return [];
  }

  return [
    { name: fieldNames.number, value: passport.Number || "" },
    {
      name: fieldNames.validity,
      value: passport.Validity || passport.ValidityDate || "",
    },
    { name: fieldNames.citizenship, value: passport.Citizenship || "" },
    {
      name: fieldNames.countryOfIssue,
      value: passport.CountryOfIssue || passport.Issued || "",
    },
  ].filter((update) => update.name);
}

function getManualPassportUpdates({
  fieldNames,
  manualPassportNumber,
}: {
  fieldNames: Record<string, string>;
  manualPassportNumber: string;
}): PassportFieldUpdate[] {
  return [
    { name: fieldNames.number, value: manualPassportNumber },
    { name: fieldNames.validity, value: "" },
    { name: fieldNames.citizenship, value: "" },
    { name: fieldNames.countryOfIssue, value: "" },
  ].filter((update) => update.name);
}

function getPassportFieldNames({
  bookingFormFields,
  passportNumberFieldName,
}: {
  bookingFormFields: BookingFormFields;
  passportNumberFieldName: string;
}): Record<PassportFieldKey, string> {
  const passengerPrefix = passportNumberFieldName.split("-")[0];

  return PASSPORT_FIELD_KEYS.reduce<Record<PassportFieldKey, string>>(
    (acc, key) => {
      const foundFieldName = PASSPORT_FIELD_ALIASES[key].find((suffix) => {
        const fullFieldName = `${passengerPrefix}-${suffix}`;
        return Object.prototype.hasOwnProperty.call(
          bookingFormFields,
          fullFieldName
        );
      });

      return {
        ...acc,
        [key]: foundFieldName ? `${passengerPrefix}-${foundFieldName}` : "",
      };
    },
    {
      number: "",
      validity: "",
      citizenship: "",
      countryOfIssue: "",
    }
  );
}
